import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationStart } from '@angular/router';
import { Events } from '@df/core/service/event';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { SearchComponentDef } from '@df/search/search.component.def';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TypewriteEffectTextDirective } from '@df/ui/display/effect/typewrite-effect/typewrite-effect-text.directive';
import { TypewriteEffectDirective } from '@df/ui/display/effect/typewrite-effect/typewrite-effect.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { KlevuSearchServiceDecorator } from '../module/klevu-search/klevu-search.service.decorator';
import { ISearchHeaderBlockContent } from './header/search-header.component';

@Component({
    selector: 'search',
    templateUrl: './search.html',
    styleUrl: './search.scss',
    standalone: true,
    imports: [FormsModule, SizeClassDirective, TypewriteEffectDirective, TypewriteEffectTextDirective, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchComponent extends SearchComponentDef implements OnInit {
    @ViewChild('input')
    searchInputElement!: ElementRef;

    @Input()
    content?: ISearchHeaderBlockContent;

    protected events = inject(Events);
    protected scrollToService = inject(ScrollToService);
    protected override searchService = inject(KlevuSearchServiceDecorator);
    protected readonly disposableTimeoutService = injectTimeoutService();

    override ngOnInit() {
        super.ngOnInit();

        this.searchService.loadKlevuVars(() => this.detectChanges());
        this.subscriptions.add(this.events.on('search.focus').subscribe(() => (this.isFocus = true)));
        this.subscriptions.add(this.events.on('search.blur').subscribe(() => this.handleBlur()));
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
        this.subscriptions.add(this.searchService.subscribeToSearch(() => this.detectChanges()));
        this.subscriptions.add(
            this.app.events.on('search.open').subscribe(value => {
                this.searchInputElement.nativeElement.focus();

                if (!value) {
                    this.reset();
                }
            })
        );
        this.subscriptions.add(
            this.app.meta.routerEvents.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.setBodyClass(false);
                }
            })
        );
    }

    get canShowPressEnter(): boolean {
        return !!this.isValid() && this.term?.length < 20;
    }

    override reset(): void {
        super.reset();

        this.detectChanges();
    }

    handleKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.code === 'Enter') {
            this.handleBlur();

            this.goToSearch();
        }

        this.detectChanges();
    }

    handleKeyDown(): void {
        this.detectChanges();
    }

    /**
     * Redirects to search page
     */
    override goToSearch(): void {
        this.searchService.quickSearch = true;
        super.goToSearch();
    }

    protected override handleFocus(): void {
        this.timeoutService.setTimeout(() => {
            super.handleFocus();
            if (this.app.isPlatformBrowser && !this.app.client.isCms) {
                this.disposableTimeoutService.setTimeout(
                    () =>
                        this.scrollToService.scrollTo(0, {
                            behavior: 'auto'
                        }),
                    0
                );
            }
        }, 500);
    }
}
